import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api, ApiError } from '../api';
import { KV, KVRow } from '../components/KV';
import Badge from '../bookings/Badge';
import { confidence_color, fmt_dt, license_color, painter_name, safe_http_url } from './helpers';
import type { BusinessDetailResponse, PainterDetail, Profile } from './types';

// The painter detail drawer (spec §5/§3) — mirrors BookingDrawer. Opened by a row click in
// DatasetDetail; on open it fetches /api/business360/businesses/:business_id (react-query
// caches per business_id so re-open doesn't refetch — the spec's "cache per row"). Renders the
// three §3 states: record present → full detail + claims + conflicts; record null + profile →
// "not yet researched"; 404 → "painter not found".

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ marginTop: 24 }}>
      <h3 style={{ marginBottom: 8 }}>{title}</h3>
      {children}
    </div>
  );
}

function RecordDetail({ record, profile }: { record: PainterDetail; profile: Profile | null }) {
  return (
    <>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 6, flexWrap: 'wrap' }}>
        {record.license_status && (
          <Badge color={license_color(record.license_status)}>
            {record.license_status}
            {record.license_class ? ` · ${record.license_class}` : ''}
          </Badge>
        )}
        {record.build_ready && <Badge color="#1f7a44">build-ready</Badge>}
        {record.confidence_overall && (
          <Badge color={confidence_color(record.confidence_overall)}>
            confidence: {record.confidence_overall}
          </Badge>
        )}
        {record.field_conflicts.length > 0 && (
          <Badge color="#9a6700">{record.field_conflicts.length} conflict{record.field_conflicts.length === 1 ? '' : 's'}</Badge>
        )}
      </div>

      <Section title="Details">
        <KV>
          <KVRow label="Business ID" value={<code>{record.business_id}</code>} />
          {record.legal_name && <KVRow label="Legal name" value={record.legal_name} />}
          {record.dba_name && <KVRow label="DBA" value={record.dba_name} />}
          {record.phone_primary && (
            <KVRow
              label="Phone"
              value={
                <span>
                  <code>{record.phone_primary}</code>
                  {record.phone_source ? <span className="muted"> · {record.phone_source}</span> : null}
                </span>
              }
            />
          )}
          {(record.address || record.city) && (
            <KVRow label="Address" value={[record.address, record.city].filter(Boolean).join(', ')} />
          )}
          {record.website && (
            <KVRow
              label="Website"
              value={(() => {
                const href = safe_http_url(record.website);
                return href ? (
                  <a href={href} target="_blank" rel="noopener noreferrer">
                    {record.website}
                  </a>
                ) : (
                  record.website
                );
              })()}
            />
          )}
          {record.email && <KVRow label="Email" value={record.email} />}
          {record.services.length > 0 && <KVRow label="Services" value={record.services.join(', ')} />}
          {record.residential_flag !== null && (
            <KVRow label="Residential" value={record.residential_flag ? 'yes' : 'no'} />
          )}
          {record.license_number && <KVRow label="License #" value={<code>{record.license_number}</code>} />}
          {record.license_expiration && <KVRow label="License expires" value={record.license_expiration} />}
          {record.license_source && <KVRow label="License source" value={record.license_source} />}
          {record.entity_status && <KVRow label="Entity status" value={record.entity_status} />}
          {record.language_primary && <KVRow label="Language" value={record.language_primary} />}
          {record.spanish_signal !== null && (
            <KVRow label="Spanish signal" value={record.spanish_signal ? 'yes' : 'no'} />
          )}
          {record.notes && <KVRow label="Notes" value={record.notes} />}
          {/* Canonical-identity sub-block from `profile` — record doesn't carry these (§3). */}
          {profile?.identifiers?.cslb_license && (
            <KVRow label="CSLB (canonical)" value={<code>{profile.identifiers.cslb_license}</code>} />
          )}
          {profile?.identifiers?.google_place_id && (
            <KVRow label="Google place ID" value={<code>{profile.identifiers.google_place_id}</code>} />
          )}
          {profile?.created_at && <KVRow label="First seen" value={fmt_dt(profile.created_at)} />}
        </KV>
      </Section>

      <Section title={`Field conflicts (${record.field_conflicts.length})`}>
        {record.field_conflicts.length === 0 ? (
          <p className="muted" style={{ margin: 0 }}>No conflicting sources — every field had a single agreed value.</p>
        ) : (
          <KV>
            {record.field_conflicts.map((c, i) => (
              <KVRow
                key={i}
                label={c.field}
                value={
                  <span style={{ fontSize: 13 }}>
                    {/* Guard the empty-values edge so we never render a dangling "→ resolved". */}
                    {(() => {
                      const pairs = Object.entries(c.values).map(([source, v]) => `${source}: ${v ?? '—'}`);
                      return pairs.length ? `${pairs.join(' · ')} → ` : '';
                    })()}
                    {c.resolved_to === null ? (
                      <em className="muted">sources disagree — not auto-resolved</em>
                    ) : (
                      <strong>resolved to {c.resolved_to}</strong>
                    )}
                  </span>
                }
              />
            ))}
          </KV>
        )}
      </Section>

      <Section title={`Verified claims (${record.verified_claims.length})`}>
        {record.verified_claims.length === 0 ? (
          <p className="muted" style={{ margin: 0 }}>No citations recorded for this record.</p>
        ) : (
          <ul style={{ margin: 0, paddingLeft: 18, fontSize: 14, display: 'grid', gap: 8 }}>
            {record.verified_claims.map((c, i) => {
              const href = safe_http_url(c.source_url);
              return (
                <li key={i}>
                  {c.claim}{' '}
                  <span className="muted" style={{ fontSize: 13 }}>
                    —{' '}
                    {href ? (
                      <a href={href} target="_blank" rel="noopener noreferrer">
                        {c.source}
                      </a>
                    ) : (
                      c.source
                    )}
                    {c.fetched_at ? ` · ${fmt_dt(c.fetched_at)}` : ''}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </Section>
    </>
  );
}

function ProfileOnly({ profile }: { profile: Profile }) {
  return (
    <>
      <p className="muted" style={{ marginTop: 8 }}>
        Not yet researched — no verified record for this painter.
      </p>
      <Section title="Canonical profile">
        <KV>
          <KVRow label="Business ID" value={<code>{profile.business_id}</code>} />
          {profile.legal_name && <KVRow label="Legal name" value={profile.legal_name} />}
          {profile.dba_name && <KVRow label="DBA" value={profile.dba_name} />}
          {profile.phone_primary && <KVRow label="Phone" value={<code>{profile.phone_primary}</code>} />}
          {(profile.address || profile.city || profile.state) && (
            <KVRow label="Address" value={[profile.address, profile.city, profile.state].filter(Boolean).join(', ')} />
          )}
          {profile.website && <KVRow label="Website" value={profile.website} />}
        </KV>
      </Section>
    </>
  );
}

export default function PainterDrawer({
  business_id,
  on_close,
}: {
  business_id: string;
  on_close: () => void;
}) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['b360_business', business_id],
    queryFn: () => api.get<BusinessDetailResponse>(`/api/business360/businesses/${encodeURIComponent(business_id)}`),
  });

  useEffect(() => {
    const on_key = (e: KeyboardEvent) => {
      if (e.key === 'Escape') on_close();
    };
    window.addEventListener('keydown', on_key);
    return () => window.removeEventListener('keydown', on_key);
  }, [on_close]);

  const title = data?.record
    ? painter_name(data.record)
    : data?.profile
      ? painter_name(data.profile)
      : 'Painter';

  return (
    <>
      <div
        onClick={on_close}
        style={{ position: 'fixed', inset: 0, background: 'rgba(15, 26, 20, 0.35)', zIndex: 40 }}
      />
      <div
        role="dialog"
        aria-modal="true"
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          bottom: 0,
          width: 'min(720px, 92vw)',
          background: 'var(--bg)',
          borderLeft: '1px solid var(--line)',
          boxShadow: '-12px 0 32px rgba(31, 93, 58, 0.12)',
          zIndex: 41,
          overflowY: 'auto',
          padding: 24,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
          <h2 style={{ margin: 0, flex: 1 }}>{title}</h2>
          <button className="secondary" onClick={on_close} style={{ padding: '4px 12px' }}>
            Close
          </button>
        </div>

        {isLoading && <p style={{ marginTop: 24 }}>Loading painter…</p>}
        {error && (
          <p className="error" style={{ marginTop: 24 }}>
            {error instanceof ApiError && error.status === 404
              ? 'Painter not found.'
              : `Failed to load: ${error instanceof ApiError ? error.reason : String(error)}`}
          </p>
        )}

        {data && data.record && <RecordDetail record={data.record} profile={data.profile} />}
        {data && !data.record && data.profile && <ProfileOnly profile={data.profile} />}
      </div>
    </>
  );
}
